import { useEffect, useMemo, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useEncryptedParams } from "../../hooks/useEncryptedParams";
import { encrypt } from "../../utils/encryption";
import apiClient from "../../services/apiClient";
import { normalizeApiError } from "../../utils/apiError";
import Button from "../../components/common/Button";
import CrudForm from "../../components/common/crud/CrudForm";
import ErrorAlert from "../../components/common/ErrorAlert";
import CrudTable from "../../components/common/crud/CrudTable";
import useCrud from "../../components/common/crud/useCrud";
import roleService from "../../services/roleService";
import alertService from "../../utils/alertService";
import { useMasterModals } from "../../hooks/useMasterModals";

const userFormSchema = [
  {
    name: "fullName",
    label: "Full Name",
    type: "text",
    placeholder: "OperatorUser",
    validation: {
      required: true,
      minLength: 3,
      maxLength: 60,
      pattern: /^[a-zA-Z][a-zA-Z\s.'-]*$/,
      patternMessage: "Full Name must contain only letters and spaces",
    },
  },
  {
    name: "email",
    label: "Email",
    type: "text",
    placeholder: "john@example.com",
    validation: {
      required: true,
      pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      patternMessage: "Enter a valid email address",
    },
  },
  {
    name: "username",
    label: "Username",
    type: "text",
    placeholder: "operator1",
    autoComplete: "off",
    validation: {
      required: true,
      minLength: 3,
      maxLength: 30,
      pattern: /^[a-zA-Z0-9._-]+$/,
      patternMessage: "Username can include letters, numbers, dot, underscore, and hyphen",
    },
  },
  {
    name: "role",
    label: "Role",
    type: "dropdown",
    placeholder: "Select role",
    options: [],
    validation: {
      required: true,
    },
  },
  {
    name: "password",
    label: "Password",
    type: "password",
    placeholder: "StrongPass1!",
    autoComplete: "new-password",
    validation: {
      required: true,
      pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^\w\s]).{8,}$/,
      patternMessage: "Password must be 8+ chars with uppercase, lowercase, number, and special character",
    },
  },
  {
    name: "isActive",
    label: "Is Active",
    type: "checkbox",
    defaultValue: true,
  },
];

function UsersModulePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useEncryptedParams();
  const [roleOptions, setRoleOptions] = useState([]);
  const [editValues, setEditValues] = useState(null);
  const [isLoadingEdit, setIsLoadingEdit] = useState(false);
  const [formError, setFormError] = useState(null);

  const isCreatePage = location.pathname.endsWith("/new");
  const isEditPage = Boolean(id);
  const isFormPage = isCreatePage || isEditPage;
  const formMode = isEditPage ? "edit" : "create";

  const basePath = useMemo(() => {
    if (location.pathname.includes("/user-master/user")) return "/user-master/user";
    return "/users";
  }, [location.pathname]);

  const loadRoles = useCallback(async () => {
    try {
      const rolePayload = await roleService.getRoles();
      const roleItems = Array.isArray(rolePayload)
        ? rolePayload
        : Array.isArray(rolePayload?.data)
        ? rolePayload.data
        : Array.isArray(rolePayload?.items)
        ? rolePayload.items
        : [];

      const options = roleItems
        .map((role) => {
          const roleName = role?.roleName || role?.name;
          if (!roleName) return null;
          return { label: roleName, value: roleName };
        })
        .filter(Boolean);

      setRoleOptions(options);
    } catch {
      setRoleOptions([]);
    }
  }, []);

  const { openModal, renderModals } = useMasterModals({
    role: loadRoles
  });

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  const endpoints = useMemo(
    () => ({
      list: "/users",
      create: "/users",
      edit: (id) => `/users/${id}`,
      update: (id) => `/users/${id}`,
      delete: (id) => `/users/${id}`,
    }),
    []
  );

  const {
    rows,
    isLoading,
    isSubmitting,
    error,
    filters,
    pagination,
    sortConfig,
    changePage,
    changeSort,
    updateFilters,
    refresh,
  } = useCrud({
    endpoints,
    idKey: "userId",
    updateMethod: "patch",
    mapEditItem: (payload) => {
      const user = payload?.data || payload || {};
      return {
        ...user,
        role: user?.role?.roleName || user?.role || "",
        password: "",
      };
    },
    transformCreateValues: (values) => {
      const nextValues = { ...values };
      if (nextValues.role && typeof nextValues.role === "object") {
        nextValues.role = nextValues.role.roleName || nextValues.role.name || "";
      }
      delete nextValues.roleId;
      return nextValues;
    },
    transformEditValues: (values) => {
      const nextValues = { ...values };
      if (nextValues.role && typeof nextValues.role === "object") {
        nextValues.role = nextValues.role.roleName || nextValues.role.name || "";
      }
      if (!nextValues.password || !String(nextValues.password).trim()) {
        delete nextValues.password;
      }
      delete nextValues.roleId;
      return nextValues;
    },
    initialFilters: { search: "" },
    initialSort: { key: "fullName", order: "ASC" },
  });

  const userTableColumns = useMemo(
    () => [
      { key: "fullName", label: "Name", sortable: true },
      { key: "email", label: "Email", sortable: true },
      { key: "username", label: "Username", sortable: true },
      { key: "role.roleName", label: "Role", sortable: false },
      {
        key: "status",
        label: "Status",
        render: (item) => (
          <span
            className={`rounded-full px-2 py-1 text-xs font-semibold ${
              item?.isActive
                ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300"
                : "bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300"
            }`}
          >
            {item?.isActive ? "Active" : "Inactive"}
          </span>
        ),
      },
    ],
    []
  );

  const formSchema = useMemo(() => {
    const schemaWithRoleOptions = userFormSchema.map((field) => {
      if (field.name !== "role") return field;
      return {
        ...field,
        options: roleOptions,
        onAdd: () => openModal("role"),
      };
    });

    if (formMode === "edit") {
      return schemaWithRoleOptions.map((field) => {
        if (field.name !== "password") return field;
        return {
          ...field,
          hint: "Leave blank to keep the current password. Enter only if you wish to change it.",
          validation: { required: false },
        };
      });
    }
    return schemaWithRoleOptions;
  }, [formMode, roleOptions, openModal]);

  useEffect(() => {
    if (!isEditPage || !id) {
      setEditValues(null);
      setFormError(null);
      return;
    }

    let isMounted = true;
    const loadEditUser = async () => {
      setIsLoadingEdit(true);
      setFormError(null);
      try {
        const response = await apiClient.get(`/users/${id}`);
        const payload = response?.data || {};
        const user = payload?.data || payload || {};
        if (!isMounted) return;
        setEditValues({
          ...user,
          role: user?.role?.roleName || user?.role || "",
          password: "",
        });
      } catch (err) {
        if (!isMounted) return;
        const normalized = normalizeApiError(err);
        setFormError(normalized.message);
      } finally {
        if (isMounted) setIsLoadingEdit(false);
      }
    };

    loadEditUser();
    return () => {
      isMounted = false;
    };
  }, [id, isEditPage]);

  const saveUser = async (values) => {
    setFormError(null);
    try {
      if (formMode === "edit" && id) {
        const nextValues = { ...values };
        if (nextValues.role && typeof nextValues.role === "object") {
          nextValues.role = nextValues.role.roleName || nextValues.role.name || "";
        }
        if (!nextValues.password || !String(nextValues.password).trim()) {
          delete nextValues.password;
        }
        delete nextValues.roleId;
        await apiClient.patch(`/users/${id}`, nextValues);
        alertService.success("Record updated successfully.");
      } else {
        const nextValues = { ...values };
        if (nextValues.role && typeof nextValues.role === "object") {
          nextValues.role = nextValues.role.roleName || nextValues.role.name || "";
        }
        delete nextValues.roleId;
        await apiClient.post("/users", nextValues);
        alertService.success("Record created successfully.");
      }
      await refresh();
      navigate(basePath, { replace: true });
    } catch (err) {
      const normalized = normalizeApiError(err);
      setFormError(normalized.message);
      alertService.error(normalized.message);
    }
  };

  const openCreate = () => navigate(`${basePath}/new`);
  const openEdit = (item) => navigate(`${basePath}/${encrypt(item?.userId)}/edit`);
  const deleteUser = async (item) => {
    const userId = item?.userId;
    if (!userId) {
      alertService.error("User ID is required.");
      return;
    }

    const result = await alertService.confirm(
      `Do you want to delete user "${item?.fullName || item?.username || userId}"?`,
      "Delete User",
    );
    if (!result?.isConfirmed) return;

    try {
      await apiClient.delete(`/users/${userId}`);
      alertService.success("User deleted successfully.");
      await refresh();
    } catch (err) {
      const normalized = normalizeApiError(err);
      alertService.error(normalized.message);
    }
  };

  if (isFormPage) {
    const notFound = formMode === "edit" && !isLoadingEdit && !editValues && !formError;

    return (
      <div className="space-y-3">
        <div className="flex justify-end">
          <Button variant="secondary" onClick={() => navigate(basePath)}>
            Back to List
          </Button>
        </div>

        {formError ? <ErrorAlert message={formError} /> : null}
        {notFound ? (
          <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">Record not found.</div>
        ) : (
          <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
            <CrudForm
              mode={formMode}
              schema={formSchema}
              initialValues={formMode === "edit" ? editValues || {} : { isActive: true }}
              isSubmitting={isSubmitting || isLoadingEdit}
              onSubmit={saveUser}
              onCancel={() => navigate(basePath)}
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {error ? <ErrorAlert message={error} /> : null}

      <CrudTable
        columns={userTableColumns}
        rows={rows}
        isLoading={isLoading}
        pagination={pagination}
        sortConfig={sortConfig}
        filters={filters}
        onFiltersChange={updateFilters}
        onSort={changeSort}
        onPageChange={changePage}
        onEdit={openEdit}
        onDelete={deleteUser}
        searchKey="search"
        searchPlaceholder="Search by name, email, or username"
        tableScrollHeight="70vh"
        toolbarAction={<Button onClick={openCreate}>Create User</Button>}
      />
      {renderModals()}
    </div>
  );
}

export default UsersModulePage;
